import { useEffect, useState, useCallback } from 'react';
import { automationApi, type RecoveryTicket } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { StatCard, PageHeader, Skeleton, EmptyState } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { RefreshCw, RotateCcw, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';

const statusLabels: Record<string, string> = {
  message_sent: 'Message Sent', awaiting_response: 'Awaiting Reply',
  customer_requested: 'Reship Requested', customer_declined: 'Customer Declined',
  no_response: 'No Response', recovery_in_progress: 'In Progress',
  reshipped: 'Reshipped', closed: 'Closed',
};
const statusColors: Record<string, string> = {
  message_sent: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  awaiting_response: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  customer_requested: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  customer_declined: 'bg-red-500/10 text-red-400 border-red-500/20',
  no_response: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  recovery_in_progress: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  reshipped: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  closed: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

export default function AutomationRecoveryPage() {
  const [tickets, setTickets] = useState<RecoveryTicket[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await automationApi.recoveryTickets();
      if (res.success) setTickets(res.data);
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (ticketId: string, action: string) => {
    try {
      await automationApi.resolveRecoveryTicket(ticketId, action);
      toast.success(`Ticket ${action}`);
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  const awaiting = tickets.filter(t => t.status === 'awaiting_response' || t.status === 'customer_requested').length;
  const reshipped = tickets.filter(t => t.status === 'reshipped').length;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <PageHeader title="Recovery Center" description="Return recovery and reshipment" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title="Recovery Center"
        description="Manage returned shipments and recovery requests"
        action={<Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Returned" value={tickets.length} />
        <StatCard label="Awaiting Action" value={awaiting} />
        <StatCard label="Reshipped" value={reshipped} />
        <StatCard label="Recovery Rate" value={tickets.length > 0 ? `${Math.round((reshipped / tickets.length) * 100)}%` : '0%'} />
      </div>

      <Card>
        <CardHeader title="Recovery Tickets" description={`${tickets.length} total`} />
        <CardBody>
          {tickets.length === 0 ? (
            <EmptyState title="No recovery tickets yet. Appear when shipments are returned." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-zinc-500 uppercase border-b border-zinc-800">
                    <th className="text-left py-2 px-2">Order</th>
                    <th className="text-left py-2 px-2">Customer</th>
                    <th className="text-left py-2 px-2">Reason</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40">
                      <td className="py-2 px-2 font-mono text-xs">{t.external_id || t.order_id?.slice(0, 8)}</td>
                      <td className="py-2 px-2">
                        <div className="font-medium text-xs">{t.customer_name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{t.customer_phone}</div>
                      </td>
                      <td className="py-2 px-2 text-xs text-zinc-400 capitalize">{t.return_type}: {t.return_reason}</td>
                      <td className="py-2 px-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${statusColors[t.status] || ''}`}>
                          {statusLabels[t.status] || t.status}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-xs text-zinc-500">{new Date(t.created_at).toLocaleDateString()}</td>
                      <td className="py-2 px-2 text-right">
                        {t.status === 'customer_requested' && (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => resolve(t.id, 'reshipped')}>
                              <RotateCcw className="h-3 w-3 mr-1" /> Reship
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => resolve(t.id, 'closed')}>
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
